/**
 * Single place for "active domain" defaulting: URL/session vs bootstrap.active_domain_key.
 * See story 2.2 — Today and Workbench stay aligned with Profile mode preference without per-route silos.
 */

/** @deprecated Use todaySessionLensStorageKey(userId) — keys must be scoped per user */
export const TODAY_DOMAIN_LENS_SESSION_KEY = 'today.domainLens.v1';

/** Backend default per models / bootstrap when unset */
export const DEFAULT_ACTIVE_DOMAIN_KEY = 'job_hunt';

/**
 * Legacy DB / users may still have active_domain_key "work" (alias for business).
 * Keep in sync with URL normalization in normalizeAreaParam.
 */
export function normalizeBootstrapDomainKey(activeDomainKey: string | null | undefined): string {
  const t = (activeDomainKey ?? '').trim();
  if (!t) return DEFAULT_ACTIVE_DOMAIN_KEY;
  return t === 'work' ? 'business' : t;
}

export function effectiveDomainFromBootstrap(activeDomainKey: string | null | undefined): string {
  return normalizeBootstrapDomainKey(activeDomainKey);
}

export function todaySessionLensStorageKey(userId: string | number | null | undefined): string {
  if (userId == null || userId === '') {
    return `${TODAY_DOMAIN_LENS_SESSION_KEY}.anonymous`;
  }
  return `${TODAY_DOMAIN_LENS_SESSION_KEY}.u${String(userId)}`;
}

/**
 * Today: optional sessionStorage override (user picked a lens this session).
 * If absent, follow bootstrap (including when preference changes in Profile).
 */
export function effectiveTodayDomainFilter(
  sessionLens: string | null | undefined,
  activeDomainKey: string | null | undefined,
  options?: { deferUntilBootstrap?: boolean },
): string | null {
  if (sessionLens !== undefined && sessionLens !== null && sessionLens !== '') {
    return sessionLens;
  }
  if (options?.deferUntilBootstrap) {
    return null;
  }
  return effectiveDomainFromBootstrap(activeDomainKey);
}

export function readTodaySessionDomainLens(userId: string | number | null | undefined): string | null {
  try {
    const key = todaySessionLensStorageKey(userId);
    let v = sessionStorage.getItem(key);
    if ((v === null || v === '') && userId != null && userId !== '') {
      const legacy = sessionStorage.getItem(TODAY_DOMAIN_LENS_SESSION_KEY);
      if (legacy) {
        sessionStorage.setItem(key, legacy);
        sessionStorage.removeItem(TODAY_DOMAIN_LENS_SESSION_KEY);
        v = legacy;
      }
    }
    if (v === null || v === '') return null;
    return v;
  } catch {
    return null;
  }
}

export function writeTodaySessionDomainLens(userId: string | number | null | undefined, domainId: string): void {
  try {
    sessionStorage.setItem(todaySessionLensStorageKey(userId), domainId);
  } catch {
    // ignore
  }
}

/** Clear persisted Today lens for this user (e.g. sign-out or "follow profile"). */
export function clearTodaySessionDomainLens(userId: string | number | null | undefined): void {
  try {
    sessionStorage.removeItem(todaySessionLensStorageKey(userId));
  } catch {
    // ignore
  }
}

/**
 * Whether a WorkItem's domain still matches the effective Workbench area filter
 * (including job_hunt synonym matching used on Today).
 */
export function itemDomainMatchesWorkbenchAreaFilter(
  itemDomain: string | null | undefined,
  areaFilter: string | null,
): boolean {
  if (areaFilter == null || areaFilter === 'all') return true;
  const d = (itemDomain ?? '').toLowerCase().trim();
  const f = areaFilter.toLowerCase().trim();
  if (f === 'job_hunt') {
    return d === 'job_hunt' || d === 'job search' || d === 'application' || d === 'applied';
  }
  return d === f;
}

/**
 * Workbench: `area` URL param is authoritative when present (deep links / bookmarks).
 * When `area` is omitted, scope to the user's active domain preference (same as Today).
 */
export function normalizeAreaParam(areaSearchParam: string | null): string | null {
  if (areaSearchParam === null) return null;
  const t = areaSearchParam.trim();
  if (t === '') return null;
  return t === 'work' ? 'business' : t;
}

export function effectiveWorkbenchAreaFilter(
  areaSearchParam: string | null,
  activeDomainKey: string | null | undefined,
  options?: { deferImplicitUntilBootstrap?: boolean },
): string | null {
  const normalized = normalizeAreaParam(areaSearchParam);
  if (normalized === null) {
    if (options?.deferImplicitUntilBootstrap) {
      return null;
    }
    return effectiveDomainFromBootstrap(activeDomainKey);
  }
  return normalized;
}
